'use client';
import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppDownloadModal from '@/components/AppDownloadModal';
import { useState } from 'react';
import { SITE_NAME } from '@/lib/site-config';

const sections = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    content: `By accessing or using IndianReviewClub ("the Platform"), you agree to be bound by these Terms & Conditions. If you do not agree to these terms, please do not use the Platform. These terms apply to all users including reviewers, brands/sellers, and visitors.`,
  },
  {
    id: 'eligibility',
    title: '2. Eligibility',
    content: `You must be at least 18 years of age and a resident of India to register as a reviewer or brand on the Platform. By creating an account, you represent and warrant that you meet these requirements and that all information you provide is accurate and complete.`,
  },
  {
    id: 'reviewer-obligations',
    title: '3. Reviewer Obligations',
    content: `As a reviewer, you agree to: (a) provide honest, unbiased, and genuine product reviews based on your actual experience; (b) not submit fake, plagiarised, or AI-generated reviews; (c) disclose any material connection to the brand; (d) comply with all applicable laws including the Consumer Protection Act, 2019; (e) not misuse the platform to earn rewards without completing genuine reviews. Violation of these obligations may result in account suspension and forfeiture of pending earnings.`,
  },
  {
    id: 'brand-obligations',
    title: '4. Brand / Seller Obligations',
    content: `As a brand or seller, you agree to: (a) provide accurate product information and ship products as described in campaigns; (b) not attempt to influence reviewers to post only positive reviews; (c) pay campaign fees as agreed; (d) comply with all applicable advertising and consumer protection laws; (e) not use reviewer data for purposes other than campaign management. Brands found attempting to manipulate reviews will be permanently banned.`,
  },
  {
    id: 'payments',
    title: '5. Payments & Payouts',
    content: `Reviewer earnings are credited to your in-platform wallet upon review approval. Minimum withdrawal threshold is ₹200. Payouts are processed via UPI or bank transfer within 3–7 business days of request. ${SITE_NAME} reserves the right to withhold payments if fraud or policy violations are detected. All payments are subject to applicable TDS deductions as per Indian tax law. Brands are billed as per the agreed campaign pricing; all fees are non-refundable once a campaign is live.`,
  },
  {
    id: 'intellectual-property',
    title: '6. Intellectual Property',
    content: `All content on the Platform including logos, design, code, and text is the property of ${SITE_NAME} and protected under applicable intellectual property laws. Reviews submitted by users are licensed to ${SITE_NAME} on a non-exclusive, royalty-free basis for use on the Platform and in marketing materials. You retain ownership of your original review content.`,
  },
  {
    id: 'prohibited',
    title: '7. Prohibited Activities',
    content: `You must not: (a) create multiple accounts to game the system; (b) sell, transfer, or share your account; (c) use bots, scripts, or automated tools; (d) post defamatory, obscene, or illegal content; (e) attempt to reverse-engineer or scrape the Platform; (f) engage in any activity that disrupts the Platform's normal operation. Violation may result in immediate account termination and legal action.`,
  },
  {
    id: 'disclaimer',title: '8. Disclaimer of Warranties',
    content: `The Platform is provided "as is" without warranties of any kind. ${SITE_NAME} does not guarantee uninterrupted access, accuracy of all content, or specific earnings for reviewers. We are not responsible for the quality of products shipped by brands or for disputes between brands and reviewers beyond our stated dispute resolution process.`,
  },
  {
    id: 'liability',title: '9. Limitation of Liability',
    content: `To the maximum extent permitted by law, ${SITE_NAME} shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Platform. Our total liability to you for any claim shall not exceed the amount you paid to us in the 3 months preceding the claim.`,
  },
  {
    id: 'termination',title: '10. Termination',
    content: `We reserve the right to suspend or terminate your account at any time for violation of these Terms or for any other reason at our sole discretion. Upon termination, your right to use the Platform ceases immediately. Pending earnings may be forfeited if termination is due to policy violations.`,
  },
  {
    id: 'governing-law',title: '11. Governing Law & Disputes',
    content: `These Terms are governed by the laws of India. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts in Bangalore, Karnataka. We encourage users to first contact our support team to resolve disputes amicably before pursuing legal remedies.`,
  },
  {
    id: 'changes',title: '12. Changes to Terms',
    content: `We may update these Terms from time to time. We will notify registered users of material changes via email or in-platform notification. Continued use of the Platform after changes constitutes acceptance of the updated Terms. We recommend reviewing these Terms periodically.`,
  },
];

export default function TermsPage() {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <Header onDownloadClick={() => setShowModal(true)} />
      <main className="min-h-screen bg-white pt-16">
        {/* Hero */}
        <section className="bg-[#0F0F1A] text-white py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-1.5 mb-6">
              <span className="text-xs font-semibold uppercase tracking-widest text-white/70">Legal</span>
            </div>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl md:text-5xl mb-4 leading-tight">
              Terms &amp; Conditions
            </h1>
            <p className="text-white/50 text-base max-w-2xl leading-relaxed">
              Please read these terms carefully before using the {SITE_NAME} platform. These terms govern your use of our services as a reviewer, brand, or visitor.
            </p>
            <p className="text-white/30 text-sm mt-4">Last updated: August 2026</p>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
          <div className="flex flex-col lg:flex-row gap-10">
            {/* TOC */}
            <aside className="lg:w-56 shrink-0">
              <div className="sticky top-24">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Contents</p>
                <nav className="space-y-1">
                  {sections?.map((s) => (
                    <a
                      key={s?.id}
                      href={`#${s?.id}`}
                      className="block text-sm text-gray-500 hover:text-primary transition-colors py-1 border-l-2 border-transparent hover:border-primary pl-3"
                    >
                      {s?.title}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Content */}
            <article className="flex-1 min-w-0 space-y-10">
              {sections?.map((s) => (
                <div key={s?.id} id={s?.id} className="scroll-mt-24">
                  <h2 className="font-heading font-bold text-xl text-gray-900 mb-3">{s?.title}</h2>
                  <p className="text-gray-600 leading-relaxed text-[15px]">{s?.content}</p>
                </div>
              ))}

              <div className="bg-orange-50 border border-orange-100 rounded-2xl p-6 mt-8">
                <p className="text-sm font-semibold text-gray-800 mb-1">Questions about these terms?</p>
                <p className="text-sm text-gray-500">
                  Contact us at{' '}
                  <a href="mailto:legal@indianreviewclub.com" className="text-primary hover:underline">
                    legal@indianreviewclub.com
                  </a>{' '}
                  and we&apos;ll be happy to clarify.
                </p>
              </div>
            </article>
          </div>
        </div>
      </main>
      <Footer />
      {showModal && <AppDownloadModal onClose={() => setShowModal(false)} />}
    </>
  );
}
